/**
 * variants/index.js — Variant Loader
 *
 * PURPOSE:
 *   Resolves the tenant variant (winery-specific UI overrides)
 *   and runs its register() before the ChatPanel composer renders.
 *
 * HOW IT WORKS:
 *   1. Each variant lives in variants/<tenant_slug>/index.js
 *   2. Exports `register({ config, shadow })` async function
 *   3. Inside register(), the variant calls
 *      registry.registerComponent / registerSlot / registerMessageKind
 *      to override or extend the base UI.
 *   4. loadVariant() looks up the variant by config.tenant_id and
 *      runs register() if found. Unknown tenants fall through to
 *      the base UI silently.
 *
 * ADDING A NEW WINERY:
 *   1. Create variants/<slug>/index.js with `export async function register({ config, shadow }) {}`
 *   2. Add `<slug>: <slug>Module` to the VARIANTS map below
 *   3. Inside register(), wire up only the overrides you need
 */

import { getState, setState } from "../core/state.js";
import { normalizeSearchBar } from "../core/normalize.js";
import * as goosecross from "./goosecross/index.js";
import * as fogartywinery from "./fogartywinery/index.js";
import * as halleckvineyard from "./halleckvineyard/index.js";
import * as hestanvineyards from "./hestanvineyards/index.js";
import * as valledellapace from "./valledellapace/index.js";
import * as wineengine from "./wineengine/index.js";
import * as lightningrockwinery from "./lightningrockwinery/index.js";
import * as pottersvineyard from "./pottersvineyard/index.js";

// Live BE tenant_id (admin / tenant-list).
const VARIANTS = {
  goosecross,
  fogartywinery,
  halleckvineyard,
  hestanvineyards,
  valledellapace,
  wineengine,
  thewineengine: wineengine,
  lightningrockwinery,
  pottersvineyard,
};

// Folder slug ↔ live tenant_id aliases. Host gets BOTH classes so variant CSS
// scoped to the folder slug still applies when the BE uses the live id.
const TENANT_ALIASES = {
  thewineengine: "wineengine",
};

function variantKey(config) {
  const id = config?.tenant_id;
  if (!id) return null;
  return String(id).toLowerCase().trim();
}

export async function loadVariant({ config, shadow }) {
  const key = variantKey(config);
  if (!key) return;

  const canonical = TENANT_ALIASES[key] || key;

  if (shadow?.host) {
    shadow.host.classList.add(`ws-tenant-${key}`);
    if (canonical !== key) {
      shadow.host.classList.add(`ws-tenant-${canonical}`);
    }
  }

  const variant = VARIANTS[key] || VARIANTS[canonical];
  if (!variant) {
    console.info(`[RedConcierge] No variant registered for tenant: ${key}`);
    return;
  }

  if (typeof variant.register === "function") {
    try {
      await variant.register({ config, shadow });
      console.info(`[RedConcierge] Variant loaded: ${key}${canonical !== key ? ` (→ ${canonical})` : ""}`);
    } catch (err) {
      console.error(`[RedConcierge] Variant failed: ${key}`, err);
    }
  }

  // BE cannot store arrays today, so guided_flow_questions falls back to a
  // variant-defined list when the API returned nothing. See CONFIG_CONTRACT.md.
  const beFlow = Array.isArray(config.guided_flow_questions) ? config.guided_flow_questions : [];
  if (beFlow.length === 0 && typeof variant.getGuidedFlow === "function") {
    try {
      const flow = await variant.getGuidedFlow(config);
      if (Array.isArray(flow) && flow.length > 0) {
        config.guided_flow_questions = flow;
      }
    } catch (err) {
      console.error(`[RedConcierge] Variant getGuidedFlow failed: ${key}`, err);
    }
  }

  // Same story for the host-page search bar: mount selectors + suggestions are
  // a nested object the BE's string-only store can't hold natively, so until
  // it's entered in admin as a JSON string a variant may ship the fallback.
  // BE config always wins. See CONFIG_CONTRACT.md `search_bar`.
  //
  // ⚠ This writes through STATE, not just the local `config` object. Several
  // variants (goosecross, valle, potters, lightningrock, fogarty, halleck) call
  // `setState({ tenant: { ...cfg } })` inside their own register(), which runs
  // just above — from that point `getState().tenant` is a COPY and `config` is
  // orphaned, so mutating `config` alone would never reach configAccessors.
  if (!config.search_bar && typeof variant.getSearchBar === "function") {
    try {
      const searchBar = normalizeSearchBar(await variant.getSearchBar(config));
      if (searchBar) {
        config.search_bar = searchBar;
        const tenant = getState().tenant;
        if (tenant && tenant !== config) {
          setState({ tenant: { ...tenant, search_bar: searchBar } });
        }
      }
    } catch (err) {
      console.error(`[RedConcierge] Variant getSearchBar failed: ${key}`, err);
    }
  }
}
